<template>
	<transition name="modal-fade">
		<div>
			<div v-if="isOpen" class="search-modal-overlay" @click.self="closeModal">
				<div class="search-modal">
					<!-- Modal Header -->
					<div class="search-modal-header" :style="searchHeaderStyle">
						<div class="container-fluid p-0">
							<div class="row align-items-center">
								<!-- Product Type Filter -->
								<div class="col">
									<select
										v-model="selectedType"
										class="form-select product-type-filter"
										@change="onFilterChange"
									>
										<option value="">Select Product Type</option>
										<option
											v-for="type in productTypeOptions"
											:key="type.value"
											:value="type.value"
										>
											{{ type.label }}
										</option>
									</select>
								</div>

								<!-- Close Button -->
								<div class="col-auto d-none d-md-block">
									<button
										@click="closeModal"
										class="btn-close-modal"
										aria-label="Close search"
									>
										<i class="bi bi-x-lg"></i>
									</button>
								</div>
							</div>

							<div v-if="showDynamicFilters" class="row mt-3 align-items-start">
								<div
									v-for="filter in dynamicFilters"
									:key="filter.taxonomy"
									class="col-md-4"
								>
									<div class="filter-select-pill">
										<label class="form-label">{{ filter.label }}</label>
										<div class="search-box">
											<i class="bi bi-search"></i>
											<input
												v-model="filterSearchTerms[filter.taxonomy]"
												type="text"
												class="form-control"
												:placeholder="filter.placeholder"
												@focus="openAutocomplete(filter.taxonomy)"
												@blur="closeAutocomplete()"
												@keydown.enter.prevent="
													selectFirstSuggestion(filter.taxonomy)
												"
											/>

											<div
												class="filter-options"
												v-if="
													isAutocompleteOpen(filter.taxonomy) &&
													autocompleteOptions(filter.taxonomy).length
												"
											>
												<button
													v-for="term in autocompleteOptions(
														filter.taxonomy
													)"
													:key="`${filter.taxonomy}-${term.slug}`"
													type="button"
													class="filter-option-pill"
													@mousedown.prevent
													@click="
														addFilterTerm(filter.taxonomy, term.slug)
													"
												>
													{{ term.name }}
												</button>
											</div>
										</div>

										<div
											v-if="selectedTermsForTaxonomy(filter.taxonomy).length"
											class="selected-pills"
										>
											<span
												v-for="term in selectedTermsForTaxonomy(
													filter.taxonomy
												)"
												:key="`${filter.taxonomy}-${term.slug}-selected`"
												class="filter-tag"
											>
												{{ term.name }}
												<button
													@click="
														removeFilterTerm(filter.taxonomy, term.slug)
													"
													:aria-label="`Remove ${term.name} filter`"
												>
													<i class="bi bi-x"></i>
												</button>
											</span>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- Modal Body -->
					<div class="search-modal-body">
						<div class="container-fluid">
							<div v-if="!selectedType" class="search-prompt">
								<i class="bi bi-search"></i>
								<h3>Select a Product Type</h3>
								<p>Choose a product type above, then refine with filters</p>
							</div>

							<!-- Loading State -->
							<div v-else-if="loading" class="loading-container">
								<div class="spinner-border text-primary" role="status">
									<span class="visually-hidden">Loading...</span>
								</div>
							</div>

							<!-- Error State -->
							<div v-else-if="error" class="error-container">
								<div class="alert alert-danger">
									<i class="bi bi-exclamation-triangle"></i>
									{{ error }}
								</div>
							</div>

							<!-- Results -->
							<div v-else class="search-results">
								<!-- Results Info -->
								<div class="results-info">
									<p class="results-count">
										Found {{ totalResults }} product{{
											totalResults !== 1 ? 's' : ''
										}}
									</p>
								</div>

								<div
									v-for="seriesGroup in seriesGroups"
									:key="`${seriesGroup.name}-${seriesGroup.accentColor}`"
									class="search-series-group"
								>
									<h4
										class="search-series-title"
										:style="{ borderBottomColor: seriesGroup.accentColor }"
									>
										<span>{{ seriesGroup.name }}</span>
										<span
											v-if="showTypeBadges && seriesGroup.productTypeLabel"
											class="search-series-type-badge"
											:style="{
												backgroundColor: seriesGroup.accentColor,
												borderColor: seriesGroup.accentColor,
											}"
										>
											{{ seriesGroup.productTypeLabel }}
										</span>
									</h4>
									<div class="row g-4 products-grid">
										<div
											v-for="product in seriesGroup.products"
											:key="product.id"
											class="col-sm-6 col-md-4 col-lg-3"
										>
											<ProductCard
												:product="product"
												@click="openProductModal"
											/>
										</div>
									</div>
								</div>

								<!-- No Results -->
								<div
									v-if="seriesGroups.length === 0 && selectedType"
									class="no-results"
								>
									<i class="bi bi-search"></i>
									<h3>No products found</h3>
									<p>Try a different search term</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Product Modal -->
			<ProductModal
				:product="selectedProduct"
				:isOpen="isProductModalOpen"
				@close="closeProductModal"
			/>
		</div>
	</transition>
</template>

<script>
import { ref, computed, watch } from 'vue';
import ProductCard from './ProductCard.vue';
import ProductModal from './ProductModal.vue';

export default {
	name: 'SearchModal',
	components: {
		ProductCard,
		ProductModal,
	},
	setup() {
		// State
		const isOpen = ref(false);
		const products = ref([]);
		const loading = ref(false);
		const error = ref(null);
		const currentPage = ref(1);
		const totalResults = ref(0);
		const selectedProduct = ref(null);
		const isProductModalOpen = ref(false);
		const selectedType = ref('');
		const dynamicFilters = ref([]);
		const dynamicFilterTerms = ref({});
		const selectedFilters = ref({});
		const filterSearchTerms = ref({});
		const activeAutocompleteTaxonomy = ref(null);

		const productTypeOptions = [
			{ value: 'ceramic', label: 'Ceramic' },
			{ value: 'rigid', label: 'Rigid' },
			{ value: 'lvt', label: 'LVT' },
			{ value: 'laminate', label: 'Laminate' },
			{ value: 'wood', label: 'Wood' },
			{ value: 'carpet', label: 'Carpet' },
			{ value: 'quartz', label: 'Quartz' },
		];

		const productTypeColors = {
			ceramic: '#e1001f',
			rigid: '#f38a1f',
			lvt: '#fcb813',
			laminate: '#008f4c',
			wood: '#0a4280',
			carpet: '#a2228e',
			quartz: '#0092b0',
		};

		const showDynamicFilters = computed(() => {
			return Boolean(selectedType.value && dynamicFilters.value.length > 0);
		});

		const searchHeaderStyle = computed(() => {
			const color = selectedType.value
				? productTypeColors[selectedType.value] || '#000'
				: '#000';
			return {
				'--search-type-color': color,
			};
		});

		const showTypeBadges = computed(() => {
			return !selectedType.value;
		});

		const seriesGroups = computed(() => {
			const groups = new Map();

			products.value.forEach(product => {
				const groupName = product.series || 'Other Products';
				const productType = product.productType || selectedType.value || '';
				const groupKey = `${groupName.toLowerCase()}::${productType}`;

				if (!groups.has(groupKey)) {
					groups.set(groupKey, {
						name: groupName,
						productType,
						productTypeLabel:
							productTypeOptions.find(option => option.value === productType)
								?.label || '',
						accentColor: productTypeColors[productType] || '#111',
						products: [],
					});
				}

				groups.get(groupKey).products.push(product);
			});

			const sortedGroups = Array.from(groups.values()).sort((left, right) => {
				if (left.name === 'Other Products') {
					return 1;
				}
				if (right.name === 'Other Products') {
					return -1;
				}

				return left.name.localeCompare(right.name, undefined, {
					sensitivity: 'base',
				});
			});

			sortedGroups.forEach(group => {
				group.products.sort((left, right) => {
					return left.title.localeCompare(right.title, undefined, {
						sensitivity: 'base',
					});
				});
			});

			return sortedGroups;
		});

		const resetDynamicFilters = () => {
			dynamicFilters.value = [];
			dynamicFilterTerms.value = {};
			selectedFilters.value = {};
			filterSearchTerms.value = {};
		};

		const fetchFilterTerms = async taxonomy => {
			try {
				const params = new URLSearchParams({
					action: 'get_taxonomy_terms',
					taxonomy,
				});

				const response = await fetch(`${window.trendsData.ajaxUrl}?${params}`);
				if (!response.ok) {
					return;
				}

				const data = await response.json();
				if (data.success) {
					dynamicFilterTerms.value[taxonomy] = data.data;
				}
			} catch (err) {
				console.error(`Error fetching terms for ${taxonomy}:`, err);
			}
		};

		const loadTypeFilters = async postType => {
			resetDynamicFilters();

			if (!postType) {
				return;
			}

			try {
				const params = new URLSearchParams({
					action: 'get_archive_filter_config',
					post_type: postType,
				});

				const response = await fetch(`${window.trendsData.ajaxUrl}?${params}`);
				if (!response.ok) {
					return;
				}

				const data = await response.json();
				if (!data.success || !Array.isArray(data.data) || data.data.length === 0) {
					return;
				}

				dynamicFilters.value = data.data;
				const initialSelections = {};
				dynamicFilters.value.forEach(filter => {
					initialSelections[filter.taxonomy] = [];
					filterSearchTerms.value[filter.taxonomy] = '';
					fetchFilterTerms(filter.taxonomy);
				});
				selectedFilters.value = initialSelections;
			} catch (err) {
				console.error('Error fetching filter config:', err);
			}
		};

		// Methods
		const openModal = () => {
			isOpen.value = true;
			document.body.style.overflow = 'hidden';
		};

		const closeModal = () => {
			isOpen.value = false;
			document.body.style.overflow = '';
			selectedType.value = '';
			products.value = [];
			currentPage.value = 1;
			error.value = null;
			resetDynamicFilters();
			activeAutocompleteTaxonomy.value = null;
		};

		const fetchAllPagesForType = async postType => {
			const perPage = 100;
			let page = 1;
			let total = 0;
			const collectedProducts = [];

			while (true) {
				const params = new URLSearchParams({
					action: 'get_archive_products',
					post_type: postType,
					page: page.toString(),
					per_page: perPage.toString(),
					orderby: 'title',
				});

				if (selectedType.value && postType === selectedType.value) {
					Object.entries(selectedFilters.value).forEach(([taxonomy, values]) => {
						if (Array.isArray(values) && values.length > 0) {
							params.append(`filters[${taxonomy}]`, values.join(','));
						}
					});
				}

				const response = await fetch(`${window.trendsData.ajaxUrl}?${params}`);
				if (!response.ok) {
					throw new Error('Failed to fetch products');
				}

				const data = await response.json();
				if (!data.success) {
					return { products: [], total: 0 };
				}

				if (page === 1) {
					total = data.data.total;
				}

				const pageProducts = (data.data.products || []).map(product => ({
					...product,
					productType: postType,
				}));
				collectedProducts.push(...pageProducts);

				if (page >= (data.data.pages || 1)) {
					break;
				}

				page++;
			}

			return {
				products: collectedProducts,
				total,
			};
		};

		const searchProducts = async () => {
			if (!selectedType.value) {
				products.value = [];
				totalResults.value = 0;
				return;
			}

			loading.value = true;

			error.value = null;

			try {
				const result = await fetchAllPagesForType(selectedType.value);

				products.value = result.products;
				totalResults.value = result.total;
			} catch (err) {
				error.value = err.message;
				console.error('Error searching products:', err);
			} finally {
				loading.value = false;
			}
		};

		const onFilterChange = async () => {
			await loadTypeFilters(selectedType.value);
			currentPage.value = 1;
			searchProducts();
		};

		const onDynamicFilterChange = () => {
			currentPage.value = 1;
			searchProducts();
		};

		const addFilterTerm = (taxonomy, slug) => {
			const selected = selectedFilters.value?.[taxonomy] || [];
			if (selected.includes(slug)) {
				filterSearchTerms.value[taxonomy] = '';
				return;
			}

			selectedFilters.value[taxonomy] = [...selected, slug];
			filterSearchTerms.value[taxonomy] = '';
			activeAutocompleteTaxonomy.value = taxonomy;

			onDynamicFilterChange();
		};

		const removeFilterTerm = (taxonomy, slug) => {
			const selected = selectedFilters.value?.[taxonomy] || [];
			selectedFilters.value[taxonomy] = selected.filter(item => item !== slug);
			onDynamicFilterChange();
		};

		const selectedTermsForTaxonomy = taxonomy => {
			const selectedSlugs = selectedFilters.value?.[taxonomy] || [];
			const terms = dynamicFilterTerms.value[taxonomy] || [];

			return selectedSlugs
				.map(slug => terms.find(term => term.slug === slug) || { slug, name: slug })
				.filter(Boolean);
		};

		const autocompleteOptions = taxonomy => {
			const query = (filterSearchTerms.value[taxonomy] || '').trim().toLowerCase();
			const terms = dynamicFilterTerms.value[taxonomy] || [];
			const selectedSlugs = selectedFilters.value?.[taxonomy] || [];

			return terms.filter(term => {
				return (
					(!query || term.name.toLowerCase().includes(query)) &&
					!selectedSlugs.includes(term.slug)
				);
			});
		};

		const isAutocompleteOpen = taxonomy => {
			return activeAutocompleteTaxonomy.value === taxonomy;
		};

		const openAutocomplete = taxonomy => {
			activeAutocompleteTaxonomy.value = taxonomy;
		};

		const closeAutocomplete = () => {
			setTimeout(() => {
				activeAutocompleteTaxonomy.value = null;
			}, 120);
		};

		const selectFirstSuggestion = taxonomy => {
			const suggestions = autocompleteOptions(taxonomy);
			if (suggestions.length > 0) {
				addFilterTerm(taxonomy, suggestions[0].slug);
			}
		};

		// Product Modal Methods
		const openProductModal = product => {
			selectedProduct.value = product;
			isProductModalOpen.value = true;
		};

		const closeProductModal = () => {
			isProductModalOpen.value = false;
			setTimeout(() => {
				selectedProduct.value = null;
			}, 300);
		};

		// Watch for escape key globally when modal is open
		watch(isOpen, newValue => {
			if (newValue) {
				const handleEscape = e => {
					if (e.key === 'Escape' && !isProductModalOpen.value) {
						closeModal();
					}
				};
				document.addEventListener('keydown', handleEscape);

				return () => {
					document.removeEventListener('keydown', handleEscape);
				};
			}
		});

		return {
			isOpen,
			products,
			loading,
			error,
			selectedType,
			productTypeOptions,
			dynamicFilters,
			dynamicFilterTerms,
			selectedFilters,
			filterSearchTerms,
			showDynamicFilters,
			searchHeaderStyle,
			showTypeBadges,
			seriesGroups,
			totalResults,
			selectedProduct,
			isProductModalOpen,
			openModal,
			closeModal,
			searchProducts,
			onFilterChange,
			onDynamicFilterChange,
			addFilterTerm,
			removeFilterTerm,
			selectedTermsForTaxonomy,
			autocompleteOptions,
			isAutocompleteOpen,
			openAutocomplete,
			closeAutocomplete,
			selectFirstSuggestion,
			openProductModal,
			closeProductModal,
		};
	},
};
</script>

<style scoped>
/* Component-specific styles - main styles will be in search-modal.scss */
</style>
